/**
 * LT-CAPTIONS — the translated-revision delivery envelope, and the switch that
 * keeps the whole caption path inert until something can fill it.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * WHY THIS EXISTS AS ITS OWN TOPIC, AND WHY IT MIRRORS THE SOURCE TURN
 *
 * The same three forces that put the source turn on its own topic apply here,
 * for the same reasons (`sourceTurnEnvelope.ts:1-31`):
 *
 *   - `translation_revision_id` is a uuid5 the DATABASE derives and
 *     `revision_index` is allocated by `translation_append_revision`
 *     (migration 20261095), so a record built at the moment the provider
 *     emitted text would be PREDICTING both;
 *   - `created_at` is written by Postgres;
 *   - and waiting for either inline would put a database round trip in front of
 *     a caption.
 *
 * So the shape is identical to the source turn's: append, read the CANONICAL
 * row back, publish THAT. This file is the wire format for the last step. It is
 * deliberately a near-copy of `sourceTurnEnvelope.ts` — two envelopes on one
 * subsystem that validate differently would be two ways to be wrong.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * WHY THE SELECTION RIDES WITH THE REVISION
 *
 * Appending a revision does NOT display it. `translationRevisionAppended`
 * (`translationSlice.ts:1570`) only adds to history; `translationRevisionSelected`
 * (`:1622`) decides what a reader sees, and `buildTurnView` (`:2377`) renders
 * `selected ? selected.text : null`. An appended-but-unselected revision shows
 * "Waiting for the translation" for ever.
 *
 * Two topics could deliver those two facts, but then their ORDER would be a
 * property of the network: selection refuses a revision that was never appended
 * (`:1639-1642`), so a reordered pair silently loses the selection and strands
 * the caption in the placeholder state. Carrying both in one envelope makes the
 * ordering a property of THIS FILE instead, which is where it can be tested.
 *
 * `selection` is nullable and names a revision id that MAY DIFFER from the one
 * being appended — appending revision 2 while revision 1 remains on screen is a
 * real thing the ledger supports, and an envelope that could not express it
 * would force the producer to lie.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * WHY `provider_elapsed_ms` IS A SIBLING, AND WHY NOTHING READS IT
 *
 * A SIBLING because `TranslationRevisionV1` is frozen with
 * `additionalProperties: false` (`contracts/live-translation/turn.v1.schema.json:153`),
 * so it cannot grow a field — exactly the reason `transcript_sync_id` is a
 * sibling of `source_turn` and never a property of it.
 *
 * NOTHING READS IT because it has not been characterised — and the careless
 * summary of why is wrong, so be exact. The measurement in
 * `worker-translateaiz/tests/fixtures/openai/README.md:146-151` was taken on
 * AUDIO deltas, where the field is a pure frame counter (200 × frame index,
 * against 400 ms of media per frame) and therefore useless. The TRANSCRIPT
 * field is a different quantity with irregular steps that fall on sentence
 * boundaries, and nobody has measured what it counts.
 *
 * ⚠️ IT IS CARRIED PRECISELY SO NOBODY RE-DERIVES IT LATER, NOT SO IT CAN BE
 * USED. It was proposed as the timing anchor for aligning a revision to a
 * source turn; it cannot be one, and captions attached to the wrong turn are
 * worse than no captions. If you are reading this because you want to order or
 * schedule something, the answer is no — use the turn lineage, which is
 * authoritative, or add a field the provider actually attests.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * THE ATTACHMENT EXTENSION (migration 20261164; sol design verdict on board
 * run 23, 2026-09-02)
 *
 * Board run 23 retracted words on the officer's live caption row: a BOUNDED
 * head-prefix revision landed and the browser, unable to tell a provisional
 * row from a final one, dropped the engine's still-rolling suffix. Sol's
 * ruling: "the worker owns attachment truth; the UI owns the no-retraction
 * display invariant ... dedupe by absolute engine-stream range, not tokens."
 * So the `revision` object now carries SIX more keys, everywhere it is
 * emitted (`translation_get_revision`, the live envelope, both hydration
 * RPCs) — the worker's `REVISION_CONTRACT_KEYS` is the exact 25-key set:
 *
 *     origin                engine | fallback | null
 *     attachment_confidence exact | bounded | joint | fallback | null
 *     attachment_basis      ^[a-z][a-z0-9_]{0,63}$ | null
 *     allocation_group_id   uuid | null
 *     engine_range_start    integer | null   half-open [start, end), ABSOLUTE
 *     engine_range_end      integer | null   per allocation group, in CODE
 *                                            POINTS (Python `str` indices);
 *                                            both null (a fallback row) or
 *                                            0 <= start <= end
 *
 * The frozen `turn.v1.schema.json` (`additionalProperties: false`) is bound
 * byte-for-byte to `contracts.ts` and shared with three other runtimes, so it
 * is NOT edited. The six are validated HERE, around the frozen core: the core
 * by the frozen validator, the extension by `validateTranslationRevisionRecord`.
 *
 * Two strictnesses, on purpose:
 *   - the WIRE (`validateRevisionEnvelopeV1`) requires the exact 25-key
 *     revision — the DB and worker ship together with this UI, and the
 *     worker refuses a 19-key body on its own side;
 *   - the RECORD validator the store uses accepts the frozen 19 OR the full
 *     25. The 19-key shape is still needed: `hooks/useHistoricalTranslationProjection`
 *     and the other-language feed strip provenance keys from projection rows
 *     before dispatch (their owner passes the six through separately). A
 *     PARTIAL set — some of the six without the others — is refused BY NAME,
 *     so that drift cannot hide behind the closed schema's generic error.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * WHAT THIS VALIDATOR DOES NOT DO
 *
 * It does not re-implement `validateTranslationRevisionV1`; it delegates. The
 * nested record's frozen core is governed by the frozen `turn.v1.schema.json`.
 *
 * It does not check that the revision's turn is known. That is the reducer's
 * rule (`translationSlice.ts:1579-1587`, `unknown_source_turn`) and a second
 * copy would be a second place to drift.
 */

import {
  validateTranslationRevisionV1,
  type TranslationRevisionV1,
} from '@/lib/translation/contracts';

/** The six attachment keys, in the worker's order (`REVISION_CONTRACT_KEYS`). */
export const REVISION_ATTACHMENT_KEYS = [
  'origin',
  'attachment_confidence',
  'attachment_basis',
  'allocation_group_id',
  'engine_range_start',
  'engine_range_end',
] as const;

export type RevisionAttachmentKey = (typeof REVISION_ATTACHMENT_KEYS)[number];

/** The closed confidence set of `translation_revisions.attachment_confidence`. */
export const REVISION_ATTACHMENT_CONFIDENCES = ['exact', 'bounded', 'joint', 'fallback'] as const;
export type RevisionAttachmentConfidence = (typeof REVISION_ATTACHMENT_CONFIDENCES)[number];

/**
 * The worker's attachment truth for one revision row. `null` values are a row
 * written before the provenance columns existed (20261163) or, for the range,
 * a fallback row whose words have no position on the engine stream.
 */
export interface RevisionAttachmentV1 {
  origin: TranslationOrigin | null;
  attachment_confidence: RevisionAttachmentConfidence | null;
  attachment_basis: string | null;
  allocation_group_id: string | null;
  /**
   * Half-open [start, end) in the worker ledger's ABSOLUTE engine-stream
   * coordinates for `allocation_group_id`, counted in CODE POINTS (Python
   * `str` indices). JavaScript strings are UTF-16: slice them with the
   * code-point helpers in `liveTranslatedCaptions.ts`, never `String#slice`.
   */
  engine_range_start: number | null;
  engine_range_end: number | null;
}

/** The 25-key wire shape: the frozen 19 plus the six attachment keys. */
export type AttachedTranslationRevisionV1 = TranslationRevisionV1 & RevisionAttachmentV1;

/** What the store keeps: the frozen 19 (hydration paths that strip) or the 25. */
export type TranslationRevisionRecordV1 = TranslationRevisionV1 | AttachedTranslationRevisionV1;

export type TranslationRevisionRecordValidation =
  | { valid: true; record: TranslationRevisionRecordV1; attachment: RevisionAttachmentV1 | null }
  | {
      valid: false;
      /** CLOSED reason, never built from the input. */
      reason:
        | 'not_an_object'
        | 'revision_invalid'
        | 'attachment_key_set_partial'
        | 'origin_invalid'
        | 'attachment_confidence_invalid'
        | 'attachment_basis_invalid'
        | 'allocation_group_id_invalid'
        | 'engine_range_invalid'
        | 'settlement_invalid';
      /** Human-readable, for the rejection log; names keys, never values. */
      detail: string;
      /** The frozen validator's errors when `reason === 'revision_invalid'`. */
      errors: readonly { instancePath: string; keyword: string }[];
    };

const ATTACHMENT_KEY_SET: ReadonlySet<string> = new Set(REVISION_ATTACHMENT_KEYS);

/**
 * D4a (TL-02, rule 121; design-d4-settlement-on-the-wire.md "Delivery order"):
 * the ONE named optional key of the revision record, accepted BEFORE any
 * producer (migration D4b) emits it. Absent = `provisional`. A closed
 * vocabulary, refused by name; any other extra key is still refused.
 */
export const REVISION_SETTLEMENT_KEY = 'settlement';
export const REVISION_SETTLEMENT_STATES = ['provisional', 'final_attached'] as const;
export type RevisionSettlement = (typeof REVISION_SETTLEMENT_STATES)[number];
const SETTLEMENT_STATE_SET: ReadonlySet<string> = new Set(REVISION_SETTLEMENT_STATES);

/** The record's settlement: the carried value, or `provisional` when absent. */
export function revisionSettlementOf(record: object): RevisionSettlement {
  const value = (record as Record<string, unknown>)[REVISION_SETTLEMENT_KEY];
  return value === 'final_attached' ? 'final_attached' : 'provisional';
}
const ATTACHMENT_CONFIDENCE_SET: ReadonlySet<string> = new Set(REVISION_ATTACHMENT_CONFIDENCES);
/** The 20261163 CHECK on `attachment_basis`. */
const ATTACHMENT_BASIS_SHAPE = /^[a-z][a-z0-9_]{0,63}$/u;
/** Any RFC-4122 textual uuid (the worker mints uuid5; the DB column is `uuid`). */
const UUID_SHAPE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/iu;

function isNullableSafeInteger(value: unknown): value is number | null {
  return value === null || (typeof value === 'number' && Number.isSafeInteger(value));
}

/**
 * Validate the six attachment values. The frozen core has already passed.
 * Returns a CLOSED reason, never a message built from the input.
 */
function validateAttachmentValues(
  record: Record<string, unknown>,
): { valid: true; attachment: RevisionAttachmentV1 } | { valid: false; reason: 'origin_invalid' | 'attachment_confidence_invalid' | 'attachment_basis_invalid' | 'allocation_group_id_invalid' | 'engine_range_invalid'; detail: string } {
  const origin = record.origin;
  if (origin !== null && !isTranslationOrigin(origin)) {
    return { valid: false, reason: 'origin_invalid', detail: 'origin is not engine|fallback|null' };
  }
  const confidence = record.attachment_confidence;
  if (
    confidence !== null &&
    (typeof confidence !== 'string' || !ATTACHMENT_CONFIDENCE_SET.has(confidence))
  ) {
    return {
      valid: false,
      reason: 'attachment_confidence_invalid',
      detail: 'attachment_confidence is not exact|bounded|joint|fallback|null',
    };
  }
  const basis = record.attachment_basis;
  if (basis !== null && (typeof basis !== 'string' || !ATTACHMENT_BASIS_SHAPE.test(basis))) {
    return {
      valid: false,
      reason: 'attachment_basis_invalid',
      detail: 'attachment_basis is not ^[a-z][a-z0-9_]{0,63}$ or null',
    };
  }
  const group = record.allocation_group_id;
  if (group !== null && (typeof group !== 'string' || !UUID_SHAPE.test(group))) {
    return {
      valid: false,
      reason: 'allocation_group_id_invalid',
      detail: 'allocation_group_id is not a uuid or null',
    };
  }
  const start = record.engine_range_start;
  const end = record.engine_range_end;
  // The 20261164 CHECK, verbatim: both null, or 0 <= start <= end. A float, a
  // negative or an unsafe integer means the producer computed rather than
  // copied the ledger's value — refused, never rounded.
  if (!isNullableSafeInteger(start) || !isNullableSafeInteger(end)) {
    return {
      valid: false,
      reason: 'engine_range_invalid',
      detail: 'engine_range_start/engine_range_end must be safe integers or null',
    };
  }
  if ((start === null) !== (end === null)) {
    return {
      valid: false,
      reason: 'engine_range_invalid',
      detail: 'engine_range_start and engine_range_end are supplied together or not at all',
    };
  }
  if (start !== null && end !== null && (start < 0 || start > end)) {
    return {
      valid: false,
      reason: 'engine_range_invalid',
      detail: 'engine range is not half-open with 0 <= start <= end',
    };
  }
  return {
    valid: true,
    attachment: {
      origin: origin as TranslationOrigin | null,
      attachment_confidence: confidence as RevisionAttachmentConfidence | null,
      attachment_basis: basis as string | null,
      allocation_group_id: group as string | null,
      engine_range_start: start,
      engine_range_end: end,
    },
  };
}

/**
 * Validate one revision RECORD as the store keeps it: the frozen 19-key core,
 * optionally extended by the six attachment keys — all six or none.
 *
 * The returned `record` is the INPUT object itself (never a copy): the store
 * keeps what it was given, so a 19-key row stays 19 keys and a 25-key row
 * keeps every attachment fact. `attachment` is the parsed six, or `null` for
 * the 19-key core (attachment UNKNOWN — never guessed).
 */
export function validateTranslationRevisionRecord(
  value: unknown,
): TranslationRevisionRecordValidation {
  if (!isPlainObject(value)) {
    return { valid: false, reason: 'not_an_object', detail: 'revision is not an object', errors: [] };
  }
  const present = REVISION_ATTACHMENT_KEYS.filter((key) => key in value);
  if (present.length !== 0 && present.length !== REVISION_ATTACHMENT_KEYS.length) {
    const missing = REVISION_ATTACHMENT_KEYS.filter((key) => !(key in value));
    return {
      valid: false,
      reason: 'attachment_key_set_partial',
      detail: `attachment key set is partial: missing ${missing.join(', ')}`,
      errors: [],
    };
  }
  // The frozen core is validated WITHOUT the six — the closed schema would
  // refuse them as additional properties — and with everything else still in
  // place, so an unknown 26th key is refused exactly as before.
  if (
    REVISION_SETTLEMENT_KEY in value &&
    !(typeof value[REVISION_SETTLEMENT_KEY] === 'string' &&
      SETTLEMENT_STATE_SET.has(value[REVISION_SETTLEMENT_KEY] as string))
  ) {
    return {
      valid: false,
      reason: 'settlement_invalid',
      detail: 'settlement is not provisional|final_attached',
      errors: [],
    };
  }
  const core: Record<string, unknown> = {};
  for (const [key, entry] of Object.entries(value)) {
    if (!ATTACHMENT_KEY_SET.has(key) && key !== REVISION_SETTLEMENT_KEY) core[key] = entry;
  }
  const validated = validateTranslationRevisionV1(core);
  if (!validated.valid) {
    return {
      valid: false,
      reason: 'revision_invalid',
      detail: 'revision fails the frozen turn contract',
      errors: validated.errors,
    };
  }
  if (present.length === 0) {
    return { valid: true, record: value as unknown as TranslationRevisionV1, attachment: null };
  }
  const attachment = validateAttachmentValues(value);
  if (!attachment.valid) {
    return { valid: false, reason: attachment.reason, detail: attachment.detail, errors: [] };
  }
  return {
    valid: true,
    record: value as unknown as AttachedTranslationRevisionV1,
    attachment: attachment.attachment,
  };
}

/**
 * The attachment carried by a record the store ALREADY admitted: the six
 * fields when present, else `null`. No validation — the reducer did that.
 */
export function revisionAttachmentOf(
  record: TranslationRevisionRecordV1,
): RevisionAttachmentV1 | null {
  if (!('attachment_confidence' in record)) return null;
  const attached = record as AttachedTranslationRevisionV1;
  return {
    origin: attached.origin,
    attachment_confidence: attached.attachment_confidence,
    attachment_basis: attached.attachment_basis,
    allocation_group_id: attached.allocation_group_id,
    engine_range_start: attached.engine_range_start,
    engine_range_end: attached.engine_range_end,
  };
}

/** The one contract version this build accepts. */
export const REVISION_ENVELOPE_CONTRACT_VERSION =
  'live-translation.revision-envelope.v1';

/**
 * The LiveKit text-stream topic a producer would publish on.
 *
 * Named in the same style as `translation_source_turn` rather than the dotted
 * `translation.route.v1`, because it belongs to the same family: a reliable
 * TEXT STREAM carrying a canonical row read back from Postgres. The dotted
 * topic is a data-channel projection of a participant attribute, which is a
 * different mechanism with different durability.
 */
export const REVISION_TOPIC = 'translation_revision';

/**
 * The LiveKit text-stream topic a producer publishes an EXPLICIT ABSENCE on, so
 * a browser that is live for a turn learns "no translation is coming" rather
 * than showing the perpetual "Translating…" artifact until it next hydrates.
 * Mirrors `revision_sender.REVISION_UNAVAILABLE_TOPIC` on the worker.
 */
export const REVISION_UNAVAILABLE_TOPIC = 'translation_revision_unavailable';

/** Exactly four top-level keys. Nothing else is accepted. */
const ENVELOPE_KEYS = [
  'contract_version',
  'revision',
  'selection',
  'provider_elapsed_ms',
] as const;

/**
 * Exactly three keys on the selection.
 *
 * The generation and the selected index ride WITH the selected id because the
 * browser's D2 fence needs all three as one fact: the id names what to show, the
 * generation is what the fence compares against so a stale live packet cannot
 * regress the display, and the index is the position the hydration read reports
 * for the same selection. The worker builds exactly this set in
 * `revision_sender.build_revision_envelope`.
 */
const SELECTION_KEYS = [
  'translation_revision_id',
  'selection_generation',
  'selected_revision_index',
] as const;

/**
 * Which revision of this pair the reader should now see.
 *
 * Not necessarily the one in `revision` — see the header.
 */
export interface RevisionSelectionV1 {
  translation_revision_id: string;
  /**
   * The selection's generation, monotonic per (source_turn_id, target_language).
   * The reducer's D2 fence no-ops a selection whose generation does not advance,
   * so a reordered or replayed live packet cannot move the display backwards.
   * A real value: generation 0 is the first selection, never "unset".
   */
  selection_generation: number;
  /** The revision_index the selected revision holds, as the ledger reports it. */
  selected_revision_index: number;
}

export interface RevisionEnvelopeV1 {
  contract_version: typeof REVISION_ENVELOPE_CONTRACT_VERSION;
  /** The exact 25-key shape (20261164): the frozen 19 plus the six attachment keys. */
  revision: AttachedTranslationRevisionV1;
  /** `null` when this revision is appended to history without being shown. */
  selection: RevisionSelectionV1 | null;
  /**
   * Provider-declared position, carried as evidence and NEVER interpreted.
   * See the header — its meaning is measured to be unknown.
   */
  provider_elapsed_ms: number | null;
}

export type RevisionEnvelopeValidation =
  | { valid: true; envelope: RevisionEnvelopeV1 }
  | { valid: false; reason: string };

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    Object.getPrototypeOf(value) !== null
  );
}

function hasExactKeys(value: Record<string, unknown>, expected: readonly string[]): boolean {
  const keys = Object.keys(value).sort();
  const wanted = [...expected].sort();
  return keys.length === wanted.length && keys.every((key, index) => key === wanted[index]);
}

/**
 * Validate one envelope. Returns a CLOSED reason, never a message built from
 * the input: this reason is counted and logged, and the input is untrusted.
 */
export function validateRevisionEnvelopeV1(value: unknown): RevisionEnvelopeValidation {
  if (!isPlainObject(value)) {
    return { valid: false, reason: 'not_an_object' };
  }

  // An EXACT key set, not "at least these". An envelope that has grown a key is
  // one this build has not been taught to read, and admitting it while ignoring
  // the extra is how a producer and a consumer quietly diverge.
  if (!hasExactKeys(value, ENVELOPE_KEYS)) {
    return { valid: false, reason: 'unexpected_key_set' };
  }

  if (value.contract_version !== REVISION_ENVELOPE_CONTRACT_VERSION) {
    return { valid: false, reason: 'contract_version_mismatch' };
  }

  // THE WIRE IS STRICT (20261164): every one of the six attachment keys must
  // be PRESENT. A 19-key revision is a producer from before the migration —
  // refused by name so the rollout gap is visible, never coerced into an
  // "attachment unknown" row that could retract words again.
  const carried = value.revision;
  if (!isPlainObject(carried)) {
    return { valid: false, reason: 'revision_invalid' };
  }
  if (!REVISION_ATTACHMENT_KEYS.every((key) => key in carried)) {
    return { valid: false, reason: 'revision_attachment_missing' };
  }
  const validated = validateTranslationRevisionRecord(carried);
  if (!validated.valid) {
    // The frozen core's failure keeps its historical reason; an attachment
    // value failure names itself (all closed strings).
    return {
      valid: false,
      reason: validated.reason === 'revision_invalid' ? 'revision_invalid' : validated.reason,
    };
  }
  const revision = validated.record as AttachedTranslationRevisionV1;

  const selection = value.selection;
  if (selection !== null) {
    if (!isPlainObject(selection)) {
      return { valid: false, reason: 'selection_not_an_object' };
    }
    if (!hasExactKeys(selection, SELECTION_KEYS)) {
      return { valid: false, reason: 'selection_unexpected_key_set' };
    }
    const selectedId = selection.translation_revision_id;
    if (typeof selectedId !== 'string' || selectedId.length === 0) {
      return { valid: false, reason: 'selection_revision_id_not_a_string' };
    }
    // The generation and the index are the fence's operands. A float here would
    // mean the producer computed rather than copied them, and a negative one is
    // outside the ledger's own range — either is a producer/consumer disagreement
    // this build must refuse rather than admit and let regress the display.
    // 0 is legitimate for BOTH: the first selection is generation 0, and a
    // turn's first revision is index 0.
    const selectionGeneration = selection.selection_generation;
    if (
      typeof selectionGeneration !== 'number' ||
      !Number.isSafeInteger(selectionGeneration) ||
      selectionGeneration < 0
    ) {
      return { valid: false, reason: 'selection_generation_not_canonical' };
    }
    const selectedIndex = selection.selected_revision_index;
    if (
      typeof selectedIndex !== 'number' ||
      !Number.isSafeInteger(selectedIndex) ||
      selectedIndex < 0
    ) {
      return { valid: false, reason: 'selection_index_not_canonical' };
    }
    // A selection naming a revision this envelope does not carry can only be
    // satisfied if that revision was appended EARLIER. The reducer refuses an
    // unappended one (`translationSlice.ts:1639-1642`), so admitting it here is
    // safe — but a selection naming a revision belonging to a DIFFERENT PAIR is
    // a producer defect the reducer cannot see, because it keys on
    // (source_turn_id, target_language) taken from the selection payload the
    // consumer builds. The consumer builds that key from `revision`, so an
    // envelope whose selection belongs elsewhere would silently retarget it.
    // There is no way to check the pair from an id alone, which is exactly why
    // the consumer derives the pair from `revision` and never from `selection`.
    void selectedId;
  }

  const elapsed = value.provider_elapsed_ms;
  if (elapsed !== null) {
    // Integer, because the provider sends an integer and a float here would
    // mean the producer computed rather than copied it.
    if (typeof elapsed !== 'number' || !Number.isSafeInteger(elapsed) || elapsed < 0) {
      return { valid: false, reason: 'provider_elapsed_ms_not_canonical' };
    }
  }

  void revision;
  return { valid: true, envelope: value as unknown as RevisionEnvelopeV1 };
}

// ════════════════════════════════════════════════════════════════════════════
// THE EXPLICIT-ABSENCE ENVELOPE
//
// A separate topic and a separate envelope from the revision, because it carries
// a DIFFERENT fact: not "here is the translated text" but "there will be no
// translated text for this turn in this language, and here is why". Without it a
// route that never produces a revision leaves the caption on the perpetual
// "Translating…" artifact for the whole call — the exact failure
// `TRANSLATED_CAPTIONS_ENABLED` exists to prevent. It mirrors the worker's
// `revision_sender.build_unavailable_envelope` shape byte-for-byte.
// ════════════════════════════════════════════════════════════════════════════

export const REVISION_UNAVAILABLE_ENVELOPE_CONTRACT_VERSION =
  'live-translation.revision-unavailable-envelope.v1';

/** Exactly six top-level keys. Nothing else is accepted. */
const UNAVAILABLE_ENVELOPE_KEYS = [
  'contract_version',
  'call_id',
  'source_turn_id',
  'target_language',
  'reason_code',
  'occurred_at_ms',
] as const;

/**
 * The CLOSED reason set, mirrored EXACTLY through migration 20261165
 * `translation_revision_unavailable_reason_check` constraint and the worker's
 * `revision_writer` marks. Any other value is a producer defect this build
 * refuses rather than renders.
 */
export const REVISION_UNAVAILABLE_REASON_CODES = [
  'no_session',
  'source_language_unknown',
  'translation_failed',
  'lease_unavailable',
  'append_refused',
  'dropped_backlogged',
  'stream_read_failed',
  'internal_error',
  'attachment_failed',
  'no_output_at_teardown',
  // D2 / TL-03 (20261228): a closed component the engine said nothing for during
  // the live call — marked, never re-translated (rule 91).
  'no_engine_output',
] as const;

export type RevisionUnavailableReasonCode =
  (typeof REVISION_UNAVAILABLE_REASON_CODES)[number];

const REVISION_UNAVAILABLE_REASON_SET: ReadonlySet<string> = new Set(
  REVISION_UNAVAILABLE_REASON_CODES,
);

export interface RevisionUnavailableEnvelopeV1 {
  contract_version: typeof REVISION_UNAVAILABLE_ENVELOPE_CONTRACT_VERSION;
  call_id: string;
  source_turn_id: string;
  target_language: string;
  reason_code: RevisionUnavailableReasonCode;
  occurred_at_ms: number;
}

export type RevisionUnavailableEnvelopeValidation =
  | { valid: true; envelope: RevisionUnavailableEnvelopeV1 }
  | { valid: false; reason: string };

/**
 * Validate one unavailable envelope. Like the revision validator, the reason it
 * returns is CLOSED and never built from the untrusted input.
 *
 * ⚠️ It does NOT check that the turn is known — that is the reducer's rule
 * (`translationTurnMarkedUnavailable` refuses an unadmitted turn), and a second
 * copy would be a second place to drift. The `source_turn_id` is validated only
 * as a non-empty string here; the reducer keys on it.
 */
export function validateRevisionUnavailableEnvelopeV1(
  value: unknown,
): RevisionUnavailableEnvelopeValidation {
  if (!isPlainObject(value)) {
    return { valid: false, reason: 'not_an_object' };
  }
  if (!hasExactKeys(value, UNAVAILABLE_ENVELOPE_KEYS)) {
    return { valid: false, reason: 'unexpected_key_set' };
  }
  if (value.contract_version !== REVISION_UNAVAILABLE_ENVELOPE_CONTRACT_VERSION) {
    return { valid: false, reason: 'contract_version_mismatch' };
  }
  if (typeof value.call_id !== 'string' || value.call_id.length === 0) {
    return { valid: false, reason: 'call_id_not_a_string' };
  }
  if (typeof value.source_turn_id !== 'string' || value.source_turn_id.length === 0) {
    return { valid: false, reason: 'source_turn_id_not_a_string' };
  }
  if (typeof value.target_language !== 'string' || value.target_language.length === 0) {
    return { valid: false, reason: 'target_language_not_a_string' };
  }
  if (
    typeof value.reason_code !== 'string' ||
    !REVISION_UNAVAILABLE_REASON_SET.has(value.reason_code)
  ) {
    return { valid: false, reason: 'reason_code_not_in_set' };
  }
  const occurredAt = value.occurred_at_ms;
  if (
    typeof occurredAt !== 'number' ||
    !Number.isSafeInteger(occurredAt) ||
    occurredAt < 0
  ) {
    return { valid: false, reason: 'occurred_at_ms_not_canonical' };
  }
  return { valid: true, envelope: value as unknown as RevisionUnavailableEnvelopeV1 };
}

// ════════════════════════════════════════════════════════════════════════════
// THE MERGED RECORD'S ORIGIN (owner design 2026-09-02, "One Transcript, Two
// Ears").
//
// ONE record per caller turn: "<original text> [<engine words>]". The
// bracketed half IS the translated caption and the translated transcript row.
// Which ARM produced a revision row's words is named by the row itself, in
// `request_contract_version` (free text 1..128 on `translation_revisions`,
// `20261092_live_translation_lineage.sql`):
//
//   - the fence-settled contract (`revision_writer.REVISION_FENCE_CONTRACT_VERSION`)
//     and the session-aligned contract (`revision_translator.
//     REVISION_ALIGNED_CONTRACT_VERSION`) carry the translate SESSION'S own
//     words — what the officer actually heard: the ENGINE;
//   - the plain terra contract (`revision_translator.REVISION_REQUEST_CONTRACT_VERSION`)
//     is a gap-fill RE-translation of the Deepgram text: the FALLBACK. A
//     fallback is an extreme condition, never the routine path (owner rule
//     2026-08-30), so a reader must be able to SEE that a row came from it.
//
// Additive: nothing above changes. A row's contract is parsed here so that no
// consumer re-derives the mapping from the literal strings.
// ════════════════════════════════════════════════════════════════════════════

/** The plain terra re-translation request — the FALLBACK arm. */
export const REVISION_REQUEST_CONTRACT_PLAIN = 'live-translation.revision-request.terra.v2';
/** terra locating the turn inside the session's own transcript — the ENGINE's words. */
export const REVISION_REQUEST_CONTRACT_ALIGNED =
  'live-translation.revision-request.terra-session-align.v1';
/** The exact-lineage fence settlement — the ENGINE's words, no model asked. */
export const REVISION_REQUEST_CONTRACT_FENCE =
  'live-translation.revision-request.translate-session-fence.v1';

/**
 * Which arm produced a revision's words. `null` is "the row does not say" —
 * never coerced into either bucket.
 */
export type TranslationOrigin = 'engine' | 'fallback';

export const TRANSLATION_ORIGINS: readonly TranslationOrigin[] = ['engine', 'fallback'];

export function isTranslationOrigin(value: unknown): value is TranslationOrigin {
  return value === 'engine' || value === 'fallback';
}

/**
 * Map a row's `request_contract_version` to its origin.
 *
 * CLOSED: only the three contracts the worker writes today are recognised. A
 * missing, empty or unknown contract is `null` — a future contract must be
 * added HERE deliberately rather than guessed from its spelling, because the
 * visible "re-translated" marker is a statement about provenance.
 */
export function translationOriginFromContract(
  requestContractVersion: string | null | undefined,
): TranslationOrigin | null {
  switch (requestContractVersion) {
    case REVISION_REQUEST_CONTRACT_FENCE:
    case REVISION_REQUEST_CONTRACT_ALIGNED:
      return 'engine';
    case REVISION_REQUEST_CONTRACT_PLAIN:
      return 'fallback';
    default:
      return null;
  }
}

/**
 * THE ONE COMPOSITION of the merged record: "<source_text> [<translation_text>]",
 * or the source alone while the turn is unsettled (no empty brackets, ever).
 * The same string the projection RPC's `merged_text` carries (P3) and the
 * transcriber's "[Caller]: ..." processing line pairs — never re-derived
 * elsewhere. No length limit on either half (owner rule 2026-08-31).
 */
export function composeMergedRecord(sourceText: string, translationText: string | null): string {
  return translationText === null ? sourceText : `${sourceText} [${translationText}]`;
}
